import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { StudentLogInSchema } from '../Builder/services/BuilderValidationSchemes';
import { signInStudent } from '../../store/thunks/user.thunk';
import Loader from '../../components/Common/Loader';
import { getURLSearchParams } from '../../helper/helper';
import { setStudentLocationAuth } from '../../store/userSlice';
import { LocationType } from './LocationSelection';
import CleverLoginButton, { CleverLoginButtonType } from '../../components/Common/CleverLoginButton';
import { CleverAuthType } from '../../services/clever.service';
import { handleStudentGoogleSignUp } from './services/student-google-signup.service';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';

const StudentAuth: React.FC = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [googleLoader, setGoogleLoader] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();
  const isLoading = useSelector((state: RootState) => state.user.isSending);
  const studentLocation = useSelector((state: RootState) => state.user.studentLocationAuth);

  const searchParams = getURLSearchParams(true);

  useEffect(() => {
    const location = searchParams.get('location') as LocationType;
    if (searchParams.get('location') && [LocationType.School, LocationType.Home].includes(location)) {
      dispatch(setStudentLocationAuth(location));
    }
  }, [searchParams]);

  const handleSignInFormSubmit = async (values: any) => {
    dispatch(signInStudent({ ...values, navigateTo }));
  };

  return (
    <div className="min-h-screen bg-whiteGrayBackground flex flex-col justify-between font-sofiaSans">
      <div></div>
      <div className="bg-whiteGrayBackground flex flex-col justify-center items-center font-sofiaSans relative pt-4">
        {/* Logo and Title */}
        <div className="max-w-xs flex flex-col items-center mb-8 px-8">
          {/* Replace with your SVG or image if available */}
          <div className="flex items-center justify-center">
            {/* Example dino SVG icon, replace as needed */}
            <img src={'/new_blue_logo.svg'} alt={'Build My Story'} />
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-secondaryStoryAdditionalLightBlueText rounded-xl px-16 py-4 flex flex-col items-center w-full max-w-sm shadow-lg">
          <Formik
            initialValues={{
              username: '',
              password: '',
            }}
            validationSchema={StudentLogInSchema}
            onSubmit={(values) => {
              handleSignInFormSubmit(values);
            }}
          >
            {({ errors, touched, values, handleChange, handleSubmit }) => (
              <form className="w-full space-y-4" onSubmit={handleSubmit}>
                <div className="relative">
                  <label
                    className="text-whiteText text-xl font-extrabold mb-2 w-full text-center block"
                    htmlFor="username"
                  >
                    Username
                  </label>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    value={values.username}
                    onChange={handleChange}
                    autoComplete="username"
                    className="appearance-none block w-full px-4 py-2 mb-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                  />
                  {touched.username && errors.username && (
                    <div className="absolute text-storyseconderror text-sm">{errors.username}</div>
                  )}
                </div>

                <div className="relative">
                  <label
                    className="text-whiteText text-xl font-extrabold mb-2 w-full text-center block"
                    htmlFor="password"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      autoComplete="current-password"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEye : faEyeSlash}
                        className="text-gray-400 hover:text-gray-600 w-5 h-5"
                      />
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <div className="absolute text-storyseconderror text-sm">{errors.password}</div>
                  )}
                </div>

                <div className="relative flex justify-center">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`px-6 py-2 h-9 mt-4 mb-2 text-secondaryStoryAdditionalLightBlueText bg-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center`}
                  >
                    Continue
                    <Loader loader={isLoading} />
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>

        <div className="flex flex-col max-w-xs w-full">
          <div className="relative py-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-secondaryBorderBackgroundLightGreyColor" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-whiteGrayBackground text-secondaryStoryBlackText">Or</span>
            </div>
          </div>

          <div className="mb-4">
            <button
              disabled={googleLoader}
              onClick={() => handleStudentGoogleSignUp(setGoogleLoader, dispatch, navigateTo, studentLocation)}
              className="w-full h-11 flex justify-center gap-2 bg-secondaryStoryOptionalLightBlueText text-white font-medium font-sofiaSans rounded-lg px-4 py-1.5 text-base items-center cursor-pointer border border-gray-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FontAwesomeIcon
                title="Sign Up with Google"
                icon={faGoogle}
                className="text-xl text-white cursor-pointer"
              />
              Continue with Google
              <Loader loader={googleLoader} />
            </button>
          </div>

          <div>
            <CleverLoginButton
              redirectUrl="/"
              authType={CleverAuthType.STUDENT_LOGIN}
              type={CleverLoginButtonType.BORDER_SIGN_IN}
              className="text-base font-bold font-sofiaSans rounded-lg px-4 py-1.5"
            >
              Sign in with Clever
            </CleverLoginButton>
          </div>
        </div>
      </div>

      {/* Teacher Link */}
      <div className="mt-6 pb-8 w-full flex justify-center">
        <span className="text-secondaryStoryBlackText text-base font-semibold text-center">
          Are you a teacher?{' '}
          <Link
            to="/auth/teacher/signup"
            className="underline hover:text-secondaryStoryAdditionalLightBlueText transition-colors"
          >
            Click here to become a Build My Story teacher for free.
          </Link>
        </span>
      </div>
    </div>
  );
};

export default StudentAuth;
